import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// The three SIP sites (Sites.SIPSiteCode IS NOT NULL). Hardcoded for now —
// promote to a real lookup proc when we add Historic Reports or anything
// else that needs the SIP-sites list dynamically.
// Valencia 1 & 3 are one group (site 5 + site 7); the qual procs filter by
// SIP_SiteGroupKey, so selecting this option loads/saves/commits both sites.
const SIP_SITES = [
    { siteID: 5, site: 'CW Valencia 1 & 3', code: 'VAL01' },
    { siteID: 6, site: 'CW Valencia 2', code: 'VAL02' },
    { siteID: 3, site: 'CW Sacramento', code: 'SAC05' },
];

// Five criteria the proc returns. `key` is the PascalCase column name (used
// in the save payload; ASP.NET binds case-insensitively). `jsonKey` is the
// camelCase property name in the GET response — written explicitly because
// System.Text.Json lowercases initial-uppercase runs as a group (so
// "DWC1Within24h" -> "dwC1Within24h", not "dWC1Within24h").
const CRITERIA = [
    { key: 'NoSafetyWarning',           jsonKey: 'noSafetyWarning',           short: 'No Warning',  long: 'Have not received a warning (oral or written) for a violation of a safety policy.' },
    { key: 'NoMissedJHA',               jsonKey: 'noMissedJHA',               short: 'JHA',         long: 'Have not missed a JHA training requirement for any employees assigned to you.' },
    { key: 'HeldTailgateMeetings',      jsonKey: 'heldTailgateMeetings',      short: 'Tailgate',    long: 'Have held all required monthly safety tailgate meetings for your department.' },
    { key: 'DWC1Within24h',             jsonKey: 'dwC1Within24h',             short: 'DWC1 24h',    long: 'DWC1 & Strategic Comp Form 5020 filed within 24 hours.' },
    { key: 'AccidentReportedWithin48h', jsonKey: 'accidentReportedWithin48h', short: 'Accident 48h',long: 'Reported all safety incidents and completed the accident investigation report within 48 hours.' },
];

function SupervisorQualManager() {
    const now = new Date();
    const access = (localStorage.getItem('admin_access_visibility') || 'ALL-ALL').toUpperCase();
    const myCode = access.split('-')[1] || 'ALL';
    const visibleSites = myCode === 'ALL' ? SIP_SITES : SIP_SITES.filter((s) => s.code === myCode);

    const [siteID, setSiteID] = useState(visibleSites[0]?.siteID || SIP_SITES[0].siteID);
    // Default to LAST month — the reporting period being qualified this month.
    const _lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [yyyymm, setYyyymm] = useState(`${_lm.getFullYear()}-${String(_lm.getMonth() + 1).padStart(2, '0')}`);
    const [year, month] = useMemo(() => {
        const [y, m] = yyyymm.split('-').map(Number);
        return [y, m];
    }, [yyyymm]);

    const [rows, setRows] = useState([]);  // server rows
    const [edits, setEdits] = useState({}); // EmployeeID -> { criterion: bool }
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');
    const [committed, setCommitted] = useState(false); // this site committed for the month?
    const [dirty, setDirty] = useState(false);         // edits since last save/commit?

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getSupervisorQualifications(siteID, year, month);
            const list = Array.isArray(res.data) ? res.data : [];
            setRows(list);
            // Seed edits from server (null => false for inputs; user can flip).
            const seed = {};
            for (const r of list) {
                seed[r.employeeID] = {};
                for (const c of CRITERIA) seed[r.employeeID][c.key] = !!r[c.jsonKey];
            }
            setEdits(seed);
            setDirty(false);
            setMsg('');
            try {
                const cs = await adminAPI.getPeriodCommitStatus(year, month);
                const mine = (cs.data || []).find((s) => s.siteID === siteID);
                setCommitted(!!mine?.isCommitted);
            } catch { setCommitted(false); }
        } catch (err) {
            flash(`Load failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
            setRows([]); setEdits({}); setCommitted(false);
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [siteID, yyyymm]);

    // Group rows by Role, preserving RoleDisplayOrder.
    const groups = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            const key = `${r.roleDisplayOrder}|${r.role || '—'}`;
            if (!map.has(key)) map.set(key, { roleDisplayOrder: r.roleDisplayOrder, role: r.role || '(no role)', rows: [] });
            map.get(key).rows.push(r);
        }
        return [...map.values()].sort((a, b) => a.roleDisplayOrder - b.roleDisplayOrder);
    }, [rows]);

    const handleEdit = (employeeID, criterionKey, checked) => {
        setDirty(true);
        setEdits((prev) => ({
            ...prev,
            [employeeID]: { ...(prev[employeeID] || {}), [criterionKey]: checked }
        }));
    };

    // Recordable answer for this site group / month, stored per qual row as
    // NoReportableForMonth: false => a recordable happened, true => clean,
    // null/undefined => not answered yet. Answered here (moved off Monthly Points).
    const reportableYes = rows.some((r) => r.noReportableForMonth === false);
    const reportableNo  = rows.length > 0 && rows.every((r) => r.noReportableForMonth === true);
    const reportableAnswer = reportableYes ? 'yes' : (reportableNo ? 'no' : 'unanswered');

    // Once the month's disbursement is committed, points are awarded — the grid
    // and the recordable answer are locked (no valid edits).
    const periodCommitted = rows.some((r) => r.periodCompleted === true);
    const locked = periodCommitted || busy || loading;

    // Site -> site group, mirroring dbo.SIP_SiteGroupKey.
    const SITE_GROUP = { 5: 'Valencia 1 & 3', 7: 'Valencia 1 & 3', 6: 'Valencia 2', 3: 'Sacramento' };
    const groupKey = SITE_GROUP[siteID] || '';

    const answerReportable = async (isReportable) => {
        const label = isReportable
            ? 'YES — a recordable happened (everyone in this group earns 0 this month)'
            : 'No — clean month';
        if (!window.confirm(`${groupKey} — ${monthLabel}\n\nRecordable during period?\n${label}`)) return;
        setBusy(true); setMsg('');
        try {
            await adminAPI.setReportable({ siteGroupKey: groupKey, reportable: isReportable, year, month });
            await reload();
            flash('Recordable answer saved.', 'success');
        } catch (err) {
            flash(`Failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setBusy(false); }
    };

    // Eligible (live preview) = all 5 criteria true AND not a reportable month.
    const isLocallyEligible = (employeeID) => {
        if (reportableYes) return false;
        const e = edits[employeeID];
        if (!e) return false;
        return CRITERIA.every((c) => e[c.key] === true);
    };

    const buildPayload = () => rows.map((r) => ({
        EmployeeID: r.employeeID,
        NoSafetyWarning:           !!edits[r.employeeID]?.NoSafetyWarning,
        NoMissedJHA:               !!edits[r.employeeID]?.NoMissedJHA,
        HeldTailgateMeetings:      !!edits[r.employeeID]?.HeldTailgateMeetings,
        DWC1Within24h:             !!edits[r.employeeID]?.DWC1Within24h,
        AccidentReportedWithin48h: !!edits[r.employeeID]?.AccidentReportedWithin48h,
    }));

    // Save = draft (also un-commits this site server-side).
    const handleSave = async () => {
        setBusy(true); setMsg('');
        try {
            await adminAPI.saveSupervisorQualifications({ siteID, year, month, rows: buildPayload() });
            await reload();
            flash('Saved (draft — not committed).', 'success');
        } catch (err) {
            flash(`Save failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setBusy(false); }
    };

    // Commit = save then mark this site final for the month. The Monthly Points
    // period can't be submitted until every site is committed.
    const handleCommit = async () => {
        if (!window.confirm(`Commit ${siteName} qualifications for ${monthLabel}?\n\nThis marks this site final. You can still edit afterward, but any change un-commits the site and you'll need to commit again before Monthly Points can be submitted.`)) return;
        setBusy(true); setMsg('');
        try {
            await adminAPI.saveSupervisorQualifications({ siteID, year, month, rows: buildPayload() });
            await adminAPI.commitSupervisorQualifications({ siteID, year, month });
            await reload();
            flash('Committed.', 'success');
        } catch (err) {
            flash(`Commit failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setBusy(false); }
    };

    // Per-column check-all (scoped to a group's rows): set one criterion for
    // every supervisor in that group in a single update.
    const setColumnForGroup = (groupRows, criterionKey, checked) => {
        setDirty(true);
        setEdits((prev) => {
            const next = { ...prev };
            for (const r of groupRows) {
                next[r.employeeID] = { ...(next[r.employeeID] || {}), [criterionKey]: checked };
            }
            return next;
        });
    };
    // 'all' | 'some' | 'none' — drives the header checkbox checked/indeterminate.
    const columnState = (groupRows, criterionKey) => {
        const vals = groupRows.map((r) => !!edits[r.employeeID]?.[criterionKey]);
        if (vals.length && vals.every((v) => v)) return 'all';
        if (vals.some((v) => v)) return 'some';
        return 'none';
    };

    // Sheet-wide controls (every supervisor, across all role groups).
    const setColumnAll = (criterionKey, checked) => setColumnForGroup(rows, criterionKey, checked);
    const columnStateAll = (criterionKey) => columnState(rows, criterionKey);
    const setEverything = (checked) => {
        setDirty(true);
        setEdits(() => {
            const next = {};
            for (const r of rows) {
                next[r.employeeID] = {};
                for (const c of CRITERIA) next[r.employeeID][c.key] = checked;
            }
            return next;
        });
    };
    const everythingState = () => {
        const vals = [];
        for (const r of rows) for (const c of CRITERIA) vals.push(!!edits[r.employeeID]?.[c.key]);
        if (vals.length && vals.every((v) => v)) return 'all';
        if (vals.some((v) => v)) return 'some';
        return 'none';
    };

    const monthLabel = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const siteName = visibleSites.find((s) => s.siteID === siteID)?.site || SIP_SITES.find((s) => s.siteID === siteID)?.site || 'this site';
    const eligibleCount = rows.filter((r) => isLocallyEligible(r.employeeID)).length;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Supervisor Qualification Maintenance</div>
                <div style={styles.title}>{monthLabel}</div>
                <div style={styles.muted}>
                    Toggle each criterion per supervisor. Eligibility is computed from all 5 being Yes.
                    Save persists this site's grid for the selected month.
                </div>
                <div style={styles.bulkRow}>
                    <label style={styles.bulkLabel}>Site:</label>
                    <select value={siteID} onChange={(e) => setSiteID(Number(e.target.value))} style={styles.input} disabled={visibleSites.length === 1}>
                        {visibleSites.map((s) => <option key={s.siteID} value={s.siteID}>{s.site}</option>)}
                    </select>
                    <label style={styles.bulkLabel}>Month:</label>
                    <input type="month" value={yyyymm} onChange={(e) => setYyyymm(e.target.value)} style={styles.input} />
                    <span style={{ ...styles.muted, marginLeft: 'auto' }}>
                        {rows.length} supervisor{rows.length === 1 ? '' : 's'} · {eligibleCount} eligible
                    </span>
                    <CommitBadge committed={committed} dirty={dirty} />
                    <button onClick={handleSave} disabled={locked || rows.length === 0} style={btn('#6b7280', locked || rows.length === 0)}>
                        {busy ? '…' : 'Save'}
                    </button>
                    <button onClick={handleCommit} disabled={locked || rows.length === 0 || (committed && !dirty)} style={btn('#15803d', locked || rows.length === 0 || (committed && !dirty))}>
                        {busy ? '…' : (periodCommitted ? 'Locked' : (committed && !dirty ? 'Committed' : 'Save & Commit'))}
                    </button>
                </div>
            </div>

            {periodCommitted && (
                <div style={{ margin: '12px 0 0', padding: '10px 14px', borderRadius: 6, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', fontSize: 13, fontWeight: 600 }}>
                    🔒 This month&rsquo;s points are committed — the qualification grid and recordable answer are read-only.
                </div>
            )}

            {/* Recordable question — answered here (moved off Monthly Points). */}
            <div style={{
                ...styles.card, marginTop: 12,
                borderColor: reportableAnswer === 'unanswered' ? '#fde68a' : (reportableYes ? '#fecaca' : '#bbf7d0'),
                background: reportableAnswer === 'unanswered' ? '#fffbeb' : (reportableYes ? '#fef2f2' : '#f0fdf4'),
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
                        Recordable during period?{' '}
                        <span style={{ color: '#6b7280', fontWeight: 600 }}>{groupKey || '—'} · {monthLabel}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => answerReportable(false)} disabled={locked || !groupKey}
                            style={choiceBtn(reportableAnswer === 'no', '#15803d', locked || !groupKey)}>
                            No
                        </button>
                        <button onClick={() => answerReportable(true)} disabled={locked || !groupKey}
                            style={choiceBtn(reportableYes, '#dc2626', locked || !groupKey)}>
                            Yes
                        </button>
                    </div>
                </div>
                <div style={{ fontSize: 12, marginTop: 8, color: reportableAnswer === 'unanswered' ? '#b45309' : (reportableYes ? '#b91c1c' : '#15803d') }}>
                    {periodCommitted ? 'This month is committed — the recordable answer is locked.' :
                     reportableAnswer === 'unanswered'
                        ? 'Not answered yet — Monthly Points can’t be committed until this is set.'
                        : reportableYes
                            ? 'A recordable occurred — everyone in this group earns 0 points this month.'
                            : 'Clean month — eligibility follows the criteria below.'}
                </div>
            </div>

            {reportableYes && (
                <div style={styles.reportableNote}>
                    ⚠ Recordable During Period — a recordable incident occurred at this location group this month.
                    Every supervisor here earns 0 points regardless of the criteria below.
                </div>
            )}

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            {!loading && groups.length > 0 && (
                <div style={{ ...styles.card, marginTop: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ ...styles.bulkLabel, display: 'flex', alignItems: 'center', gap: 6, color: '#111827' }}>
                        <input
                            type="checkbox"
                            checked={everythingState() === 'all'}
                            disabled={locked}
                            ref={(el) => { if (el) el.indeterminate = everythingState() === 'some'; }}
                            onChange={(e) => setEverything(e.target.checked)}
                        />
                        Check ALL — whole sheet
                    </label>
                    <span style={styles.muted}>or one criterion across every role group:</span>
                    {CRITERIA.map((c) => {
                        const st = columnStateAll(c.key);
                        return (
                            <label key={c.key} style={{ ...styles.bulkLabel, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }} title={c.long}>
                                <input
                                    type="checkbox"
                                    checked={st === 'all'}
                                    disabled={locked}
                                    ref={(el) => { if (el) el.indeterminate = st === 'some'; }}
                                    onChange={(e) => setColumnAll(c.key, e.target.checked)}
                                />
                                {c.short}
                            </label>
                        );
                    })}
                </div>
            )}

            {loading ? (
                <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.muted}>Loading…</div></div>
            ) : groups.length === 0 ? (
                <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.muted}>No supervisors found at this site.</div></div>
            ) : (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {groups.map((g) => (
                        <div key={g.role} style={styles.card}>
                            <div style={styles.groupHeader}>{g.role}</div>
                            <table style={styles.grid}>
                                <thead style={styles.gridHead}>
                                    <tr>
                                        <th style={styles.th}>Supervisor</th>
                                        {CRITERIA.map((c) => (
                                            <th key={c.key} style={{ ...styles.th, textAlign: 'center', width: 90 }} title={c.long}>{c.short}</th>
                                        ))}
                                        <th style={{ ...styles.th, textAlign: 'center', width: 100 }}>Eligible</th>
                                    </tr>
                                    <tr>
                                        <th style={{ ...styles.th, fontStyle: 'italic', fontWeight: 600, color: '#6b7280' }}>check all →</th>
                                        {CRITERIA.map((c) => {
                                            const st = columnState(g.rows, c.key);
                                            return (
                                                <th key={c.key} style={{ ...styles.th, textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={st === 'all'}
                                                        disabled={locked}
                                                        ref={(el) => { if (el) el.indeterminate = st === 'some'; }}
                                                        onChange={(e) => setColumnForGroup(g.rows, c.key, e.target.checked)}
                                                        title={`Check/uncheck "${c.short}" for all in ${g.role}`}
                                                    />
                                                </th>
                                            );
                                        })}
                                        <th style={styles.th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.rows.map((r) => {
                                        const elig = isLocallyEligible(r.employeeID);
                                        return (
                                            <tr key={r.employeeID}>
                                                <td style={styles.td}>{r.lastName}, {r.firstName}</td>
                                                {CRITERIA.map((c) => (
                                                    <td key={c.key} style={{ ...styles.td, textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!edits[r.employeeID]?.[c.key]}
                                                            disabled={locked}
                                                            onChange={(e) => handleEdit(r.employeeID, c.key, e.target.checked)}
                                                        />
                                                    </td>
                                                ))}
                                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                                    <EligibilityBadge ok={elig} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Commit state for the current site/month.
function CommitBadge({ committed, dirty }) {
    let label, style;
    if (committed && !dirty) { label = '✓ Committed'; style = { background: '#dcfce7', color: '#15803d', borderColor: '#bbf7d0' }; }
    else if (committed && dirty) { label = 'Edited — re-commit'; style = { background: '#fef3c7', color: '#b45309', borderColor: '#fde68a' }; }
    else if (dirty) { label = 'Unsaved'; style = { background: '#fef3c7', color: '#b45309', borderColor: '#fde68a' }; }
    else { label = 'Not committed'; style = { background: '#f3f4f6', color: '#6b7280', borderColor: '#e5e7eb' }; }
    return <span style={{ ...styles.badge, ...style }}>{label}</span>;
}

function EligibilityBadge({ ok }) {
    const style = ok
        ? { background: '#dcfce7', color: '#15803d', borderColor: '#bbf7d0' }
        : { background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' };
    return <span style={{ ...styles.badge, ...style }}>{ok ? 'ELIGIBLE' : 'NOT ELIGIBLE'}</span>;
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

// Yes/No answer pills: outlined until chosen, then filled so the current
// answer is obvious at a glance. Fixed width + nowrap so they don't wrap.
const choiceBtn = (active, color, disabled) => ({
    background: active ? color : 'white',
    color: active ? 'white' : '#374151',
    border: `1px solid ${active ? color : '#d1d5db'}`,
    padding: '6px 0', minWidth: 62, borderRadius: 6,
    fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2 },
    muted: { color: '#6b7280', fontSize: 13 },
    reportableNote: { margin: '12px 0', padding: '10px 14px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, fontWeight: 700 },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    groupHeader: { fontSize: 13, fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 },
    grid: { width: '100%', borderCollapse: 'collapse' },
    gridHead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '6px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', border: '1px solid', borderRadius: 4 }
};

export default SupervisorQualManager;
